import React, {useCallback, useEffect, useState} from "react";
import {Localizer} from "../../infrastructure/localization/localizer";
import {useMediaQuery} from "react-responsive";
import {mobileMaxWidthQuery} from "../../core/layout/responsive";
import {RoutePaths} from "../../infrastructure/routes";
import {useHistory} from "react-router-dom";
import {EvalueringsstatistikPageConstants} from "./components/EvalueringsstatistikPageConstants";
import {AccountService} from "../../services/account/accountService";
import {ReactComponent as CreditCardTimeout} from "../../content/icons/credit-card-timeout.svg";
import {
    clearDataset,
    evalueringsstatistikReducer,
    EvalueringsstatistikState,
    setSelectedFilter,
    setShouldFetchDefaultVisning,
    setShowDefaultDatasaet,
    setTabState
} from "./evalueringsstatistikSlice";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {CardButtonWithIcon} from "../../core/components/button/cardButtonWithIcon";
import {toDate, toDateFromApiFormat, tryToBool} from "../../index";
import {EvalueringsstatistikTabEnum} from "./evalueringsstatistikTabEnum";
import {LinkPlacementEnum} from "../../core/components/link/LinkPlacementEnum";
import {HelpLinks} from "../../core/components/link/helpLinks";
import {FilterType} from "../../core/sharedmodels/filter/filterType";
import {toDropdownOptions} from "../../core/sharedmodels/forSelectModel";
import {DropdownOption} from "../../core/components/dropdown/dropdown";
import useLogbogApi from "../../core/hooks/useLogbogApi";
import classNames from "classnames";
import {applyFilters} from "./evalueringsstatistikFiltersSlice";
import {FilterItemType} from "../../core/sharedmodels/filter/filterItemType";
import {IdAndNameModel} from "../../core/sharedmodels/IdAndNameModel";
import {RootTypeEnum} from "../../core/sharedmodels/laegeInstitution/rootTypeEnum";
import {GetUddannelsestypeTranslation} from "../../core/sharedmodels/stilling/uddannelseTypeEnum";
import {UddannelsesstedModel} from "../../core/sharedmodels/laegeInstitution/uddannelsesstedModel";
import {NotificationModule} from "ditmer-embla";
import {EvalueringsstatistikFiltering} from "./evalueringsstatistikFiltering";
import {setSlideinState, SlideInStateEnum} from "../../core/components/slideIn/slideInSlice";
import useUser from "../../core/hooks/useUser";

export function Evalueringsstatistik() {

    const IsMobile = useMediaQuery(mobileMaxWidthQuery);
    const history = useHistory();
    const userService = new AccountService();
    const user = userService.getUser();
    const dispatch = useAppDispatch();
    const evalueringsstatistikSliceState = useAppSelector(evalueringsstatistikReducer) as EvalueringsstatistikState;

    const [minutterSidenAendret, setMinutterSidenAendret] = useState<number | undefined>();
    const [openEditOwnFilterModal, setOpenEditOwnFilterModal] = useState<boolean>(false);
    const [mineFiltreSelectOptions, setMineFiltreSelectOptions] = useState<DropdownOption<string>[]>([]);
    const {logbogFilterApi, evalueringstatistikApi} = useLogbogApi();

    const editOwnFilterModalId = "editOwnFilterModalId";

    const { currentUser } = useUser();

    useEffect(() => {
        dispatch(setShowDefaultDatasaet(EvalueringsstatistikPageConstants.getDefaultvisning));
        if (evalueringsstatistikSliceState.lastChangedAt !== undefined)
            setMinutterSidenAendret(Math.abs(new Date().getMinutes() - (toDate(evalueringsstatistikSliceState.lastChangedAt).getMinutes())));
    }, [dispatch, evalueringsstatistikSliceState.lastChangedAt])

    const goToCreateStatisticWithDefaultView = () => {
        dispatch(clearDataset());
        dispatch(setShouldFetchDefaultVisning(true));
        dispatch(setSelectedFilter(undefined))
        dispatch(setTabState(EvalueringsstatistikTabEnum.Uddannelsessteder.toString()));
        history.push(RoutePaths.CreateEvalueringsstatistik.url);
    }

    const goToCreateStatisticWithDefaultEnkelteEvalueringerView = () => {
        dispatch(clearDataset());
        dispatch(setShouldFetchDefaultVisning(true));
        dispatch(setSelectedFilter(undefined))
        dispatch(setTabState(EvalueringsstatistikTabEnum.Evalueringer.toString()));
        history.push(RoutePaths.CreateEvalueringsstatistik.url);
    }

    const createCleanStatistikUdtraek = () => {
        dispatch(clearDataset());
        dispatch(setShouldFetchDefaultVisning(false));
        dispatch(setSelectedFilter(undefined))
        history.push(RoutePaths.CreateEvalueringsstatistik.url);
    }

    const goToCreateLatestDatasets = () => {
        dispatch(setShouldFetchDefaultVisning(false));
        dispatch(setSelectedFilter(undefined))
        history.push(RoutePaths.CreateEvalueringsstatistik.url);
    }

    const setEvalueringsstatistikDataFromExistingFilter = async (filterId: string) => {
        const filter = await logbogFilterApi.getEvalueringsstatistikFilterWithItems(filterId);
        const filterItems = filter.filterItems;

        const includeInactive = tryToBool(filterItems.filter(x => x.filterItemType === FilterItemType.IncludeInactive).first()?.value);
        const omraader = filterItems?.filter(x => x.filterItemType === FilterItemType.Omraade);
        const sygehuse = filterItems?.filter(x => x.filterItemType === FilterItemType.Hospital);
        const regioner = filterItems?.filter(x => x.filterItemType === FilterItemType.Region);
        const uddannelsessteder = filterItems?.filter(x => x.filterItemType === FilterItemType.Uddannelsessted);
        const afdelingerselect = await evalueringstatistikApi.getAfdelingerSelect(regioner?.map(x => x.value) ?? [], sygehuse?.map(x => x.value) ?? [], includeInactive);
        const afdelingerList = UddannelsesstedModel.FromSelectModel(afdelingerselect);
        const praksisSelect = await evalueringstatistikApi.getPraksisSelect(regioner?.map(x => x.value) ?? [], omraader?.map(x => x.value) ?? [], includeInactive);
        const praksisList = UddannelsesstedModel.FromSelectModel(praksisSelect);

        const allUddannelsessteder = [...afdelingerList, ...praksisList];
        allUddannelsessteder.forEach(x => x.selected = uddannelsessteder?.map(y => y.value)?.includes(x.id) ?? false);

        const filterLaegeinstitutionsType = filterItems.filter(x => x.filterItemType === FilterItemType.Laegeinstitutionstype).first()?.value;

        dispatch(applyFilters({
            regioner: regioner?.map(x => {
                return {id: x.value, name: x.value} as IdAndNameModel
            },),
            omraader: omraader?.map(x => {
                return {id: x.value, name: x.value} as IdAndNameModel
            }),
            sygehuse: sygehuse?.map(x => {
                return {id: x.value, name: x.value} as IdAndNameModel
            }),
            specialer: filterItems.filter(x => x.filterItemType === FilterItemType.Speciale).map(x => {
                return {id: x.value, name: x.value} as IdAndNameModel
            }),
            uddannelsessteder: allUddannelsessteder,
            includeInactive: tryToBool(filterItems.filter(x => x.filterItemType === FilterItemType.IncludeInactive).first()?.value),
            startDato: filterItems.filter(x => x.filterItemType === FilterItemType.Date).map(x => toDateFromApiFormat(x.value)).sort((a, b) => a.getTime() - b.getTime()).first().toIsoDateTimeString(),
            slutDato: filterItems.filter(x => x.filterItemType === FilterItemType.Date).map(x => toDateFromApiFormat(x.value)).sort((a, b) => b.getTime() - a.getTime()).first().toIsoDateTimeString(),
            uddannelsestyper: filterItems.filter(x => x.filterItemType === FilterItemType.UddannelseType).map(x => ({
                id: x.value.toString(),
                name: GetUddannelsestypeTranslation(parseInt(x.value))
            } as IdAndNameModel)),
            laegeinstitutionstype: parseInt(filterLaegeinstitutionsType) === RootTypeEnum.Praksis ? RootTypeEnum.Praksis : parseInt(filterLaegeinstitutionsType) === RootTypeEnum.Sygehus ? RootTypeEnum.Sygehus : undefined,
            datasetTitle: filter.titel,
        }));

        dispatch(setShouldFetchDefaultVisning(false));
        if (!evalueringsstatistikSliceState.dataset.some(x => x.activeFilterId === filter.id))
            dispatch(setSelectedFilter(filter.id))
    }

    const goToCreateStatisticWithOwnFilterView = async (filterId: string) => {
        await setEvalueringsstatistikDataFromExistingFilter(filterId);
        history.push(RoutePaths.CreateEvalueringsstatistik.url);
    }

    const editOwnFilter = async (filterId: string) => {
        await setEvalueringsstatistikDataFromExistingFilter(filterId);
        setOpenEditOwnFilterModal(true);
    }

    const fetchMySavedSearch = useCallback(async () => {
        //get filter dropdown options
        const filters = await logbogFilterApi.getFilterSelectOptions(FilterType.EvalueringsstatistikFilter);
        const mineFiltre = filters.filter(f => f.groupText === Localizer.forloebOverviewPage_mineFiltre()).sortBy(x => x.text);
        const mineFiltreSelectOptions = toDropdownOptions(mineFiltre)
        setMineFiltreSelectOptions(mineFiltreSelectOptions);
    },[logbogFilterApi])

    useEffect(() => {
        if (currentUser.Authenticated)
            fetchMySavedSearch();
    }, [currentUser.Authenticated, fetchMySavedSearch])


    const deleteFilter = async (filterId: string) => {
        const deletedFilter = await logbogFilterApi.DeleteFilter(filterId)
        if (deletedFilter)
            NotificationModule.showSuccess(Localizer.forloebOverviewPage_filterDeleted(), "");
        setMineFiltreSelectOptions(mineFiltreSelectOptions.filter(x => x.value !== filterId));
    }

    const searchSavedCallback = () => {
        fetchMySavedSearch();
        dispatch(setSlideinState({state: SlideInStateEnum.Closing, slideInId: editOwnFilterModalId}))
        setOpenEditOwnFilterModal(false)
    }

    return (
        <div>
            <div className="card">
                <div className="card-body wrap d-flex align-items-center">
                    <h3>{Localizer.evalueringsstatistikPage_header()}</h3>
                    <HelpLinks linkPlacement={LinkPlacementEnum.EvalueringsstatistikPage}
                               additionalClasses={"margin-left-s"}/>
                </div>
                <div className="card-body">
                    <p>{Localizer.evalueringsstatistikPage_information()}</p>
                    <h4 className="margin-top-l">{Localizer.global_anyQuestions()}</h4>
                    <p>{Localizer.evalueringsstatistikPage_questionsInformation()}</p>
                </div>
                {user.Authenticated && EvalueringsstatistikPageConstants.getComplexView
                    ?
                    <div className="col-sm-12 ">
                        <div className=" alert alert-info" role={"alert"}>
                            {Localizer.evalueringPage_bagLoginInfoTekst()}
                        </div>
                    </div>
                    :
                    <div className="col-sm-12 ">
                        <div className=" alert alert-info" role={"alert"}>
                            {Localizer.evalueringPage_offentligInfoTekst()}
                        </div>
                    </div>
                }
                <div className="card-body">
                    <div className={`flex-column ${IsMobile ? "margin-m" : ""}`}>
                        <div className={`d-flex wrap`}>
                            {evalueringsstatistikSliceState.showDefaultDatasaet &&
                                <CardButtonWithIcon icon={"list"}
                                                    confirmCallback={() => goToCreateStatisticWithDefaultEnkelteEvalueringerView()}
                                                    additionalClasses={`shadow-none ${IsMobile ? "" : "margin-right-m"}`}
                                                    buttonBottomText={Localizer.evalueringsstatistikPage_mineEvalueringer()}
                                                    buttonBottomInfoText={Localizer.evalueringsstatistikPage_mineEvalueringerInfo() + ((evalueringsstatistikSliceState.dataset?.length > 0) ? " " + Localizer.evalueringsstatistikPage_startNytUdtraekInfo() : "")}
                                />
                            }
                            {evalueringsstatistikSliceState.showDefaultDatasaet &&
                                <CardButtonWithIcon icon={"graph"}
                                                    confirmCallback={() => goToCreateStatisticWithDefaultView()}
                                                    additionalClasses={`shadow-none ${IsMobile ? "" : "margin-right-m"}`}
                                                    buttonBottomText={Localizer.evalueringsstatistikPage_mineUddannelsessteder()}
                                                    buttonBottomInfoText={Localizer.evalueringsstatistikPage_mineUddannelsesstederInfo() + ((evalueringsstatistikSliceState.dataset?.length > 0) ? " " + Localizer.evalueringsstatistikPage_startNytUdtraekInfo() : "")}
                                />
                            }
                            {evalueringsstatistikSliceState.dataset.length > 0 &&
                                <CardButtonWithIcon icon={<CreditCardTimeout/>}
                                                    confirmCallback={() => goToCreateLatestDatasets()}
                                                    additionalClasses={`shadow-none ${IsMobile ? "" : "margin-right-m"}`}
                                                    buttonBottomText={Localizer.evalueringsstatistikPage_arbejdVidere()}
                                                    buttonBottomInfoText={`${Localizer.evalueringsstatistikPage_arbejdVidereInfo()}
                                                    ${minutterSidenAendret !== undefined
                                                        ? Localizer.evalueringsstatistikPage_sidstAendret() + " "
                                                        + minutterSidenAendret + " " + ((minutterSidenAendret === 1) ? Localizer.evalueringsstatistikPage_minutSiden() : Localizer.evalueringsstatistikPage_minutterSiden())
                                                        : ""}`}/>
                            }
                            <CardButtonWithIcon icon={"plus"}
                                                confirmCallback={() => createCleanStatistikUdtraek()}
                                                isCreate={true}
                                                additionalClasses={"shadow-none"}
                                                buttonBottomText={Localizer.evalueringsstatistikPage_startNytUdtraek()}
                                                buttonBottomInfoText={Localizer.evalueringsstatistikPage_startNytUdtraekInfo()}/>
                        </div>
                    </div>
                    {mineFiltreSelectOptions.length > 0 && currentUser.Authenticated &&
                        <div className={classNames("border-top flex-container-column ")}>
                            <h4 className={"margin-top-m"}>{Localizer.evalueringsstatistikPage_mineSoegninger()}</h4>
                            <p>{Localizer.evalueringsstatistikPage_mineFiltreInfo()}</p>
                            <div className={`d-flex wrap`}>
                                {mineFiltreSelectOptions.map((filter, index) => {
                                    return (
                                        <CardButtonWithIcon key={filter.value}
                                                            icon={"filter"}
                                                            confirmCallback={() => goToCreateStatisticWithOwnFilterView(filter.value)}
                                                            additionalClasses={`shadow-none ${IsMobile ? "" : "margin-right-m"}`}
                                                            buttonBottomText={filter.label}
                                                            hideTooltip
                                                            smallCard
                                                            overrideColor={evalueringsstatistikSliceState.dataset.filter(x => x.activeFilterId === filter.value).first()?.datasetColor}
                                                            deleteCallback={() => deleteFilter(filter.value)}
                                                            editCallback={() => editOwnFilter(filter.value)}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    }
                </div>
            </div>
            <EvalueringsstatistikFiltering
                setOpenModal={setOpenEditOwnFilterModal}
                openModal={openEditOwnFilterModal}
                modalId={editOwnFilterModalId}
                isLoading={false}
                setIsLoading={() => {}} //redundant for this site - since we dont load in datasets
                isEditView
                searchSavedUpdatedCallback={() => searchSavedCallback()}
            />
        </div>
    )
}

